import React from 'react';
import { XIcon, MapPinIcon, HeartIcon, UserIcon, PhoneIcon } from 'lucide-react';
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (page: 'home' | 'shop' | 'product') => void;
}
export function MobileMenu({
  isOpen,
  onClose,
  onNavigate
}: MobileMenuProps) {
  if (!isOpen) return null;
  const handleNav = (page: 'home' | 'shop' | 'product') => {
    onNavigate(page);
    onClose();
  };
  const categories = [{
    name: 'Dog',
    emoji: '🐕'
  }, {
    name: 'Cat',
    emoji: '🐈'
  }, {
    name: 'Bird',
    emoji: '🦜'
  }, {
    name: 'Fish',
    emoji: '🐠'
  }, {
    name: 'Poultry',
    emoji: '🐔'
  }, {
    name: 'Reptile',
    emoji: '🦎'
  }, {
    name: 'Small Animal',
    emoji: '🐰'
  }, {
    name: 'Large Animal',
    emoji: '🐴'
  }, {
    name: 'Garden',
    emoji: '🌿'
  }, {
    name: 'Pool',
    emoji: '🏊'
  }];
  return <div className="fixed inset-0 z-50 flex" data-id="element-515">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity animate-fade-in" onClick={onClose} data-id="element-516" />

      <div className="relative w-4/5 max-w-sm bg-white h-full shadow-2xl flex flex-col animate-slide-in-right" data-id="element-517">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-518">
          <img src="https://cdn.magicpatterns.com/uploads/dmbfFx8UTLKDZ7YQXuKt3w/BPAG_website_logo.svg" alt="Better Pets and Gardens" className="h-8 object-contain" data-id="element-519" />
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors active:scale-95 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Close menu" data-id="element-520">
            <XIcon className="w-6 h-6" data-id="element-521" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-522">
          {/* Store Selector */}
          <div className="p-4 border-b border-gray-100 bg-brand-blue-50/50" data-id="element-523">
            <div className="flex items-center gap-3" data-id="element-524">
              <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-brand-blue" data-id="element-525">
                <MapPinIcon className="w-5 h-5" data-id="element-526" />
              </div>
              <div data-id="element-527">
                <div className="text-xs text-gray-500 font-medium" data-id="element-528">
                  Your Store
                </div>
                <div className="text-sm font-heading font-bold text-gray-900" data-id="element-529">
                  Select a Store
                </div>
              </div>
            </div>
          </div>

          {/* Main Nav */}
          <nav className="p-3 border-b border-gray-100" data-id="element-530">
            <ul className="space-y-0.5" data-id="element-531">
              {categories.map(cat => <li key={cat.name} data-id="element-532">
                  <button onClick={() => handleNav('shop')} className="w-full flex items-center gap-3 py-3 px-3 text-left font-heading font-semibold text-gray-900 hover:bg-gray-50 rounded-xl active:scale-[0.98] active:bg-gray-100 transition-all min-h-[48px]" data-id="element-533">
                    <span className="text-lg w-7 text-center" data-id="element-534">{cat.emoji}</span>
                    <span className="text-[15px]" data-id="element-535">{cat.name}</span>
                  </button>
                </li>)}
              <li className="pt-2 border-t border-gray-100 mt-2" data-id="element-536">
                <button onClick={() => handleNav('shop')} className="w-full flex items-center gap-3 py-3 px-3 text-left font-heading font-semibold text-red-600 hover:bg-red-50 rounded-xl active:scale-[0.98] transition-all min-h-[48px]" data-id="element-537">
                  <span className="text-lg w-7 text-center" data-id="element-538">🔥</span>
                  <span className="text-[15px]" data-id="element-539">Specials</span>
                </button>
              </li>
              <li data-id="element-540">
                <button className="w-full flex items-center gap-3 py-3 px-3 text-left font-heading font-semibold text-gray-900 hover:bg-gray-50 rounded-xl active:scale-[0.98] transition-all min-h-[48px]" data-id="element-541">
                  <span className="text-lg w-7 text-center" data-id="element-542">📍</span>
                  <span className="text-[15px]" data-id="element-543">Stores</span>
                </button>
              </li>
            </ul>
          </nav>

          {/* Secondary Nav */}
          <div className="p-3 space-y-1" data-id="element-544">
            <button className="w-full flex items-center gap-3 py-3 px-3 text-left font-medium text-gray-700 hover:bg-gray-50 rounded-xl active:scale-[0.98] transition-all min-h-[48px]" data-id="element-545">
              <UserIcon className="w-5 h-5 text-gray-400" data-id="element-546" />
              <span className="text-[15px]" data-id="element-547">Sign In / Register</span>
            </button>
            <button className="w-full flex items-center gap-3 py-3 px-3 text-left font-medium text-brand-blue hover:bg-brand-blue-50 rounded-xl active:scale-[0.98] transition-all min-h-[48px]" data-id="element-548">
              <HeartIcon className="w-5 h-5" data-id="element-549" />
              <span className="text-[15px]" data-id="element-550">Care Club</span>
            </button>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="p-4 border-t border-gray-100 bg-warm-50" data-id="element-551">
          <button className="w-full flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-800 font-heading font-semibold py-3 rounded-xl shadow-sm active:scale-[0.98] min-h-[48px]" data-id="element-552">
            <PhoneIcon className="w-5 h-5" data-id="element-553" />
            Call Your Store
          </button>
        </div>
      </div>
    </div>;
}